import {
  type BrandConfig,
  type LegalConfig,
  type LegalDocumentLinkSlot,
  brandName,
  legalLinkLabel,
} from '@kitup/brand';
import type { Dictionary } from './ru.js';

const number = (value: number): string => value.toLocaleString('en-US');
const noun = (count: number, singular: string, plural: string): string =>
  count === 1 ? singular : plural;

/**
 * As `ruRedrawCost`: null — no limit, undefined — count unknown, 0 — a pack is needed,
 * 'payment' — an unpaid book, redrawn only after paying.
 */
function enRedrawCost(remaining: number | null | undefined | 'payment'): string {
  if (remaining === 'payment') return 'it can only be redrawn once the book is paid for.';
  if (remaining === null) return 'it can be drawn again.';
  if (remaining === undefined) return 'redrawing it uses one redo.';
  if (remaining === 0) return 'the free redos are used up: redrawing it needs a redo pack.';
  return `redrawing it uses one free redo (${number(remaining)} left).`;
}

export function createEn(brand: BrandConfig, legal: LegalConfig): Dictionary {
  const name = brandName(brand, 'en');
  const legalLink = (slot: LegalDocumentLinkSlot): string =>
    legalLinkLabel(brand, legal, slot, 'en');
  return {
    app: {
      name,
      sections: 'Sections',
      tagline: 'A personalized children’s book in ten minutes',
    },
    meta: {
      title: `${name} — personalized children’s books`,
      description: 'Create a personalized children’s book starring your child in ten minutes.',
      ogImageAlt: brand.ogImageAlt['en'] ?? '',
    },
    demo: {
      title: 'Mira and the Kind Star',
      subtitle: 'Demo book',
      dedication: 'For those who cannot read yet, but already know how to listen.',
      bannerTitle: 'This is a demo book',
      bannerText:
        'How a finished book reads: turn pages with the arrows, a swipe, or the slider. The pictures here are simple sketches — yours are drawn from your photos.',
      start: 'Create my book',
      toBooks: 'My books',
      home: 'Home',
      spreads: [
        'Mira found an old map in the attic.\nIt showed hills, a river, and a three-pointed mark.\n“That must be a star,” Mira said.',
        'Long-Eared Bunny did not want to leave the house.\nBut Mira held his paw, and suddenly it was not scary at all.\nThe road unfolded beneath their feet.',
        'Beyond the hills, a kind star really was shining.\nIt showed the way to anyone traveling together.\nMira and Bunny walked on and counted their steps.',
        'A warm light waited for them in the window at home.\nMira hid the map under her pillow.\n“We’ll go again tomorrow,” she whispered.',
      ],
    },
    onboarding: {
      title: 'Your first book in four steps',
      steps: [
        {
          title: 'Basics',
          text: 'Pick the book’s plan, its story style and layout. The plan is fixed once the story is written; the rest you can change.',
        },
        {
          title: 'Characters',
          text: 'Fill in a profile and add three photos. We draw the character; you approve it.',
        },
        {
          title: 'Story',
          text: 'Describe what happens in a few sentences, and we’ll arrange the story into spreads.',
        },
        {
          title: 'Illustrations and export',
          text: 'The first spread is drawn free. Pay once to draw the rest and both covers, then share the book or download it.',
        },
      ],
      start: 'Start my first book',
      demo: 'Browse the demo book first',
    },
    placeholder: { underConstruction: 'The app is being built. More screens are coming soon.' },
    common: {
      loading: 'Loading…',
      reconnecting: 'The server is restarting. Reconnecting…',
      submit: 'Continue',
      saving: 'Saving…',
      saved: 'Saved.',
      retry: 'Try again',
      back: 'Back',
      close: 'Close',
      previousImage: 'Previous image',
      nextImage: 'Next image',
      imageCount: (current: number, total: number) => `${number(current)} of ${number(total)}`,
      editImage: 'Edit',
      imageEditGuidance: 'What to change in this image',
      imageEditPlaceholder: 'For example: make the scarf blue and the background lighter',
      imageEditHint: 'We keep the rest of the picture and change only what you describe.',
      applyImageEdit: 'Make this change',
      imageEditing: 'Drawing…',
      cancel: 'Cancel',
      email: 'Email',
      password: 'Password',
      name: 'Your name',
      emailPlaceholder: 'you@example.com',
      unexpectedError: 'Something went wrong. Please try again.',
      networkError: 'Could not reach the server. Check your connection.',
      generationRef: (id: string) => `Reference code: ${id}`,
      generationFailed: 'This didn’t work this time. Try again; if it keeps failing, contact us.',
      replyWithin: (hours: number) => `We usually reply within ${hours} hours.`,
    },
    auth: {
      backToSignIn: 'Back to sign in',
      signIn: 'Sign in',
      signUp: 'Sign up',
      signOut: 'Sign out',
      haveAccount: 'Already have an account?',
      noAccount: 'New here?',
      forgotLink: 'Forgot your password?',
      login: {
        title: 'Sign in',
        submit: 'Sign in',
        failed: 'Incorrect email or password',
      },
      register: {
        title: 'Create an account',
        closedTitle: 'Registration is temporarily closed',
        closedSubtitle: 'New accounts are currently created by an administrator',
        closedText: 'Contact the service administrator if you need access.',
        submit: 'Create account',
        passwordHint: 'At least 10 characters. Use a phrase instead of a single word.',
        sentTitle: 'Check your email',
        sentTo: (email: string) =>
          `We sent a confirmation link to ${email}. Open it to finish signing up.`,
        wrongAddress: 'Wrong address or no email after a few minutes? Check spam, or',
        startOver: 'sign up again',
        weakPassword: 'This password is too easy to guess. Add more words or use a phrase.',
        offerLink: legalLink('registerTerms'),
        legalJoin: 'and the',
        privacyLink: 'privacy policy',
        /** Clickwrap: an unticked box is a refusal, so the sentence is a checkbox label. */
        termsPrefix: 'I accept the',
        /*
         * The age gate only. Who may upload a photograph of a particular child is asked at the
         * upload itself, where that child exists and the answer can be enforced.
         */
        adultLabel: 'I am 18 or older',
      },
      forgot: {
        title: 'Reset password',
        subtitle: 'We’ll email you a password reset link',
        submit: 'Send reset link',
        sent: 'If an account exists for this address, the reset email is on its way. The link is valid for 30 minutes.',
      },
      reset: {
        title: 'New password',
        subtitle: 'Choose a password you have not used before',
        submit: 'Save password',
        done: 'Your password has been changed. You can now sign in.',
        badLink: 'This link is invalid or has expired. Request a new one.',
        requestNew: 'Request a new link',
      },
      verify: {
        title: 'Confirm your email',
        titleDone: 'Email confirmed',
        pending: 'Checking the link…',
        done: 'Email confirmed. You’re ready to go.',
        failed:
          'This link is invalid or has already been used. If you already confirmed your email, just sign in.',
        toSignIn: 'Sign in',
        alreadyConfirmed: 'Your email is already confirmed. You’re all set.',
        toApp: 'Go to my books',
        bannerTitle: 'Confirm your email',
        bannerText: (email: string) =>
          `We sent a link to ${email}. Until you confirm it, you can’t start making books.`,
        resend: 'Send the email again',
        resent: 'The email has been sent again.',
      },
      validation: {
        emailRequired: 'Enter your email',
        emailInvalid: 'Check the address — it may contain a typo',
        passwordRequired: 'Enter your password',
        passwordShort: 'The password must be at least 10 characters',
        nameRequired: 'What should we call you?',
        /* Names the same document the link points at, whatever the brand calls it. */
        termsRequired: `Accept the ${legalLink('registerTerms')} and the privacy policy`,
        adultRequired: 'Confirm that you are 18 or older',
      },
    },
    errors: {
      quotaExceeded:
        'You have reached your quota. The monthly generation limit resets at the beginning of next month; you can archive an extra book.',
      rateLimited: 'Too many requests. Wait a minute and try again.',
      emailNotVerified: 'Confirm your email first — we sent you a link.',
      queueUnavailable: 'We can’t start drawing right now. Try again in a minute.',
      bookPublished:
        'This book is published and cannot be edited. Unpublish it from the Export step first.',
      bookNotPublishable: 'This book has no spreads yet. Create the story before publishing.',
      looksNotReady:
        'Refresh the plan and approve outfits and recurring references before drawing.',
      charactersNotApproved: 'Approve all of the book’s characters first.',
      generationInProgress: 'This is already being created. Wait for it to finish and try again.',
      storyDraftNotReady: 'Prepare the story draft first.',
      notFound: 'Not found. Refresh the page — it may have been deleted already.',
      payloadTooLarge: 'That file is too large. Upload a smaller one.',
      unsupportedMediaType: 'That file format is not supported.',
      audioDurationInvalid:
        'This recording is too short or too long. A voice sample runs 6 to 30 seconds; a spread’s recording, up to 3 minutes.',
      audioUnreadable:
        'We couldn’t read this audio file. Record again here, or save it as MP3, M4A or WAV and upload that.',
      photoConsentRequired: 'Confirm the photo consent before uploading.',
      publicationConsentRequired:
        'Confirm the consent to publishing before opening the book by link.',
      variantNotSelected: 'Pick the variant you think looks right first.',
      styleLocked:
        'The style is locked by the first finished spread. Copy the book to use another style.',
    },
    usage: {
      title: 'Usage',
      generations: (used: number, limit: number) =>
        limit === 0
          ? `Generations this month: ${number(used)}`
          : `Generations this month: ${number(used)} of ${number(limit)}`,
      books: (used: number, limit: number) =>
        limit === 0 ? `Books: ${number(used)}` : `Books: ${number(used)} of ${number(limit)}`,
      cost: (units: string) => `Spent: ${units} units`,
      bookCost: (units: string) => `This book cost: ${units} units`,
      nearLimit: 'You are close to your quota.',
      exhausted: 'Quota reached — nothing new can be drawn until it resets.',
    },
    settings: {
      title: 'Settings',
      accountMenu: 'Account menu',
      profile: {
        title: 'Your account',
        changePassword: 'Change password',
        changePasswordHint: '— we’ll email you a link.',
      },
      language: {
        title: 'Language',
        description: 'The interface language. Text language is chosen separately for each book.',
        label: 'Interface language',
        options: { ru: 'Русский', en: 'English' },
        saved: 'Language saved.',
      },
      billing: {
        title: 'Billing',
        description:
          'Each book is paid for once, on its own. What you bought is shown on the book.',
      },
    },
    account: {
      delete: 'Delete account',
      title: 'Delete account',
      /*
       * Says what the privacy policy says, because the person reads this one instead of it.
       * It used to promise immediate, irreversible destruction while the policy promised a
       * quarantine the person can still call off — so the only screen that matters told them
       * the opposite of their actual rights. The period is interpolated: it is
       * `PURGE_QUARANTINE_DAYS`, and a deployment may set it differently.
       */
      warning: (quarantine: string) =>
        `Access closes at once: nothing stays visible and you cannot sign in. Your books, characters and photographs are then held for ${quarantine} before they are destroyed, and during that time you can write to us to stop it. After that the files are erased from storage and it cannot be undone.`,
      password: 'Enter your password to confirm',
      confirm: 'Delete permanently',
      cancel: 'Keep my account',
      wrongPassword: 'Incorrect password.',
      done: (quarantine: string) =>
        `Your account is closed. The files are destroyed after ${quarantine}; until then you can write to us to stop it.`,
      writeToUs: 'Write to us:',
    },
    books: {
      title: 'My books',
      empty: 'No books yet. Here is the whole way from photos to a finished book.',
      add: 'Add book',
      create: 'Create book',
      bookTitle: 'Working title',
      bookTitleHint:
        'Only you see this. The title on the cover comes with the story, and you can change it.',
      bookLanguage: 'Book language',
      bookLanguageHint: 'The story and the title will be in this language.',
      printSize: 'Book size',
      printSizeHint: 'The pictures are drawn for this size, and the print PDF comes out at it.',
      languages: {
        ru: 'Russian',
        en: 'English',
        uz: 'Uzbek',
        ko: 'Korean',
        ka: 'Georgian',
        tr: 'Turkish',
        tt: 'Tatar',
        fr: 'French',
        es: 'Spanish',
      },
      edited: (date: string) => `Edited ${date}`,
      spreadsDrawn: (drawn: number, total: number) =>
        `${number(drawn)} of ${number(total)} spreads drawn`,
      published: 'Published',
      open: 'Open',
      duplicate: 'Duplicate',
      archive: 'Archive',
      status: { draft: 'Draft', generating: 'Generating', ready: 'Ready', archived: 'Archived' },
    },
    support: {
      characterTitle: (name: string) => `Help with “${name}”`,
      renditionTitle: (styleId: string) => `Variants in style ${styleId}`,
    },
    library: {
      consent: {
        label:
          'Everyone in the photos I upload is me, my child, or someone who agreed to it, and I consent to processing these photos.',
        hint: 'Photos are used only for the drawings. We remove metadata and store files privately. This covers every photo in your account.',
        required: 'Consent to photo processing before uploading.',
        documentLink: 'Consent terms',
        given: 'You consented to photo processing.',
        manage: 'Change in Settings',
        settingsTitle: 'Photo processing',
      },
      photos: {
        title: 'Photos',
        deletedAfterApproval:
          'You approved this drawing, so we deleted the photos it was made from. It’s kept for every book.',
        uploadAgain: 'Upload new photos to redraw',
        deletedNeedNewStyle:
          'The photos were deleted after an earlier drawing was approved. This book uses a different style, so it needs a new drawing: upload photos again for a good likeness.',
        hintCharacter:
          'Add three shots: front, three-quarter view, and full body. Following the checklist improves the likeness.',
        hintThing: 'Add one to three photos of the item from different angles.',
        hintLocation: 'Photos are optional; you can describe the place in words.',
        add: 'Choose file',
        uploadPhoto: 'Upload',
        replace: 'Replace',
        addPhotos: 'Add photos',
        remove: 'Remove',
        uploading: 'Uploading photo…',
        slots: { front: 'Front', profile: 'Three-quarter', full: 'Full body', extra: 'Extra' },
        checklistTitle: 'Photo checklist',
        checklist: {
          daylight: 'Use daylight, preferably near a window',
          noFilters: 'No filters or beauty mode',
          eyesOpen: 'Eyes open and face unobstructed',
          plainBackground: 'Plain background, with only this character in the frame',
          sharp: 'Sharp image with the face clearly visible',
        },
        issues: {
          slot_missing: 'This shot is missing',
          low_resolution: 'Resolution is too low and the face may look blurry',
          duplicate_of_other_slot: 'This file is already used in another slot',
        },
        upload: {
          too_large: 'The file is larger than 15 MB. Reduce it or choose another file.',
          unsupported_type: 'Unsupported format. Use JPEG, PNG, WebP, or HEIC.',
          too_small: 'The short side is under 512 px, which will reduce the likeness.',
          failed: 'Could not upload the photo. Try again.',
        },
      },
      prompt: {
        title: 'Prompt',
        compose: 'Build prompt',
        hint: 'The prompt is sent to the model in English. Build it from the profile and edit it if needed.',
        empty: 'The prompt has not been built yet.',
      },
      rendition: {
        noStyle: 'Choose a book style in the Basics step first.',
        generate: 'Draw',
        regenerate: 'Regenerate',
        approve: 'Approve',
        approved: 'Approved',
        select: 'Select',
        selected: 'Selected',
        variantsTitle: 'Variants',
        zoom: (index: number) => `View variant ${number(index)} full screen`,
        empty: 'No variants yet.',
        statuses: {
          empty: 'Not drawn yet',
          generating: 'Drawing…',
          needs_review: 'Pick the one that looks right',
          approved: 'Approved',
          failed: 'Failed',
        },
        approveDeletesPhotos:
          'Approving keeps this drawing for every book and deletes the photos it was made from.',
        drawWithoutPhotosConfirm:
          'There are no photos yet, so the drawing won’t look like them — and it uses an attempt. Draw anyway?',
        redrawWithoutPhotosConfirm:
          'The photos were deleted when you approved this drawing, so a new one is drawn without them and won’t look like the person. Upload new photos first to keep the likeness. Draw anyway?',
        warnings: {
          photos_missing: 'There are no photos, so the likeness will be limited.',
          photos_incomplete: 'Not all three photos are uploaded, so the likeness may be weaker.',
          photo_low_resolution: 'One of the photos is too small.',
        },
        attempts: (count: number) => (count === 1 ? 'Drawn once' : `Drawn ${number(count)} times`),
      },
      deadlock: {
        title: 'The photos may be the problem',
        intro:
          'Three attempts have already failed. More generation is unlikely to help: the source photos, not the model, are usually the cause.',
        photosTitle: 'Uploaded photos',
        noPhotos: 'There are no photos, so the character’s appearance cannot be identified.',
        fixPhotos: 'Replace photos',
        callHuman: 'Ask for help',
        commentLabel: 'Describe what looks wrong',
        sent: 'Request sent. We’ll review it manually and get back to you.',
        failed: 'Could not send the request. Try again.',
        generateAnyway: 'Try generating again anyway',
      },
      usage: {
        unused: 'Not used in any books',
        inBooks: (count: number) => `In ${number(count)} ${noun(count, 'book', 'books')}`,
        styles: (count: number) => `Drawn in ${number(count)} ${noun(count, 'style', 'styles')}`,
        spreads: (count: number) => `${number(count)} ${noun(count, 'spread', 'spreads')}`,
        blockedDelete: 'In a book you’re working on, so it can be archived but not deleted.',
      },
      picker: {
        reuseTitle: 'From your library',
        reuseHint:
          'Already in your library. Adding one here reuses it — its drawing and details come with it.',
        newTitle: 'Or create a new one',
        usedIn: (books: number) =>
          books === 1 ? 'In 1 other book' : `In ${number(books)} other books`,
        notUsed: 'Not in a book yet',
        chooseKind: 'Choose…',
      },
      actions: {
        archive: 'Archive',
        delete: 'Delete',
        save: 'Save',
        addToBook: 'Add to book',
        removeFromBook: 'Remove from book',
        openLibrary: 'Manage your library',
        inBook: 'In book',
      },
    },
    characters: {
      title: 'Characters',
      empty: 'No characters yet. Create the first one; you can complete the profile later.',
      emptyHint: 'A character lives in your library and can move from one book to another.',
      add: 'Add character',
      create: 'Create character',
      addAnother: 'Add another character',
      stepTitle: 'Characters',
      stepSubtitle:
        'Add the people and pets in the story — toys and favorite things come next. We draw each one in this book’s style.',
      lead: 'Main character',
      leadHint:
        'If no main character is selected, the first one is used. You can select more than one.',
      stepEmpty: 'This book has no characters yet. Add at least one before creating the story.',
      stepEmptyHint: 'Add one from your library above, or create a new one.',
      stepEmptyHintFirst: 'Type a name above to start — you’ll add photos next.',
      kinds: { child: 'Child', adult: 'Adult', pet: 'Pet' },
      genders: { unspecified: 'Let the story decide', male: 'He / him', female: 'She / her' },
      moreDetails: 'More details (optional)',
      fields: {
        name: 'Name',
        displayName: 'Name used at home',
        displayNameHint: 'What the family calls them, e.g. “Bubba”. Used in the story’s dialogue.',
        roleHint: 'Who they are in this story, e.g. “big sister” or “the brave one”.',
        catchphraseHint: 'Something they always say. The story may use it once or twice.',
        kind: 'Type',
        role: 'Role in the book',
        ageYears: 'Age',
        gender: 'Gender',
        traits: 'Personality',
        traitsHint: 'Three to five words, one per line',
        favouriteActivities: 'Favorite activities',
        catchphrase: 'Catchphrase',
      },
    },
    things: {
      title: 'Favorite things',
      empty: 'No favorite things yet.',
      emptyHint: 'A favorite toy or pair of boots can appear in scenes and on the cover.',
      add: 'Add',
      create: 'Add favorite thing',
      stepTitle: 'Favorite things',
      stepSubtitle: 'Favorite things appear in scenes and on the cover.',
      optional: 'This step is optional. You can create a complete book without adding things.',
      stepEmpty: 'There are no things in this book, and that’s fine. You can continue.',
      noApprovalNeeded:
        'No approval needed: the selected drawing goes into the scenes. Pick another to swap it.',
      drawn: 'Drawn',
      categories: {
        toy: 'Toy',
        clothing: 'Clothing',
        dish: 'Cup, plate or bottle',
        furniture: 'Furniture',
        other: 'Other',
      },
      fields: { name: 'Name', category: 'Category', description: 'Description' },
    },
    locations: {
      title: 'Locations',
      empty: 'No places yet. They’re optional — the story will invent its own.',
      emptyHint: 'Locations keep recurring places visually consistent between scenes.',
      add: 'Add location',
      create: 'Create location',
      stepTitle: 'Locations',
      stepSubtitle:
        'A real place from your child’s life — the backyard, grandma’s kitchen. We draw it once and keep it the same in every scene set there.',
      optional:
        'This step is optional. A book without saved locations can still be completed and exported.',
      stepEmpty: 'No places in this book, and that’s fine. The story will invent its own.',
      noApprovalNeeded: 'No approval needed. The drawing is used in scenes as soon as it is ready.',
      spreadsTitle: 'Spreads',
      kinds: {
        room: 'Room',
        kitchen: 'Kitchen',
        outdoor: 'Outdoors',
        transport: 'Transport',
        imaginary: 'Imaginary place',
        other: 'Other',
      },
      fields: { name: 'Name', kind: 'Place type', description: 'Description' },
    },
    story: {
      title: 'Story',
      subtitle: 'Describe the story in your own words and AI will arrange it into spreads.',
      ai: {
        intro:
          'We add your characters, favorite things and places for you — no need to describe them again.',
        fields: {
          wishes: 'Story ideas',
          wishesHint:
            'A few sentences about what happens, how it ends, and anything to avoid. For example: “Leo loses his toy moose in the snow and follows tiny hoof prints to find it before dinner. No scary parts.”',
          wishesCounter: (current: number, max: number) => `${number(current)} / ${number(max)}`,
          wishesTooLong: (max: number) =>
            `Story ideas cannot be longer than ${number(max)} characters.`,
        },
        generate: 'Generate story',
        generateLocksPlan: 'Once the story is generated, the book’s plan can no longer be changed.',
        approvedLocked:
          'The story is approved. You can edit individual spreads; generating or approving another story is no longer available.',
        referenceLimit: 'One spread has too many characters. Use fewer characters in that scene.',
        regenerate: 'Regenerate',
        generating: 'Generating story…',
        status: {
          queued: 'Queued',
          running: 'Writing the story…',
          ready: 'Ready to review',
          failed: 'Failed',
        },
        preview: 'Preview',
        coverTitle: 'Title on the cover',
        coverTitleHint: 'AI suggested a title from the story. Review or change it before approval.',
        editHint: 'You can edit any spread. Changes stay in this draft.',
        save: 'Save text',
        saving: 'Saving…',
        saved: 'Text saved.',
        textFit: {
          left: (left: number, target: number) =>
            `Best kept under ${number(target)} characters: ${number(left)} left`,
          longer: (target: number) =>
            `Longer than the comfortable ${number(target)} characters, but it fits`,
          smaller: (target: number) =>
            `Longer than the comfortable ${number(target)} characters, so the type will be smaller`,
          overflow: (over: number) =>
            `Doesn't fit this layout even in the smallest type: about ${number(over)} ${noun(over, 'character', 'characters')} too many. Shorten the text, or it will cover the picture`,
        },
        staleConfirm: (remaining: number | null | undefined | 'payment') =>
          `The picture on this spread was drawn for the earlier text. Once you save, it will be marked as no longer matching the story, and ${enRedrawCost(remaining)} Save?`,
        promptUpdating: 'Updating the illustration description and visual references…',
        promptFailed:
          'The illustration description could not be updated. Save the text again to retry.',
        applyLocks:
          'Approval fixes the cast and locks story regeneration. You can still edit individual spreads.',
        apply: 'Approve story and preview a spread',
        applyNeedsCharacters: (names: string) =>
          `The story is drawn with approved characters. Approve the drawing of: ${names}.`,
        applied: 'Applied',
        replaceConfirm:
          'This book already has illustrations. A new story will detach them from the spreads, but the files will remain in the library. Continue?',
        underused: (name: string, count: number) =>
          `${name} appears in only ${number(count)} ${count === 1 ? 'spread' : 'spreads'}; aim for at least two. Regenerate, or mention ${name} in another spread’s text.`,
        history: 'Draft history',
        draftDate: (date: string) => `Draft from ${date}`,
        failed: 'The story could not be generated. You can retry; your ideas have been saved.',
        current: 'Current book story',
        currentHint:
          'This is the approved story. You can edit the text of individual spreads here.',
        adminUnapply: 'Unapprove story',
        adminUnapplying: 'Unapproving…',
        adminUnapplyHint:
          'Administrator only: return the book to editing its characters, things, locations, and story draft.',
        adminUnapplyConfirm:
          'Unapprove this story? Current spreads, illustration variants, videos, narration, covers, and finished exports will be removed from the book. The story draft will be kept and become editable again.',
        adminUnapplied:
          'Story approval removed. Characters, things, locations, and the story draft can be edited again.',
      },
      manual: {
        intro:
          'Each item is one spread. Text blocks can be moved and recolored independently of the illustration.',
        empty: 'No spreads yet. Add the first one or apply an AI draft.',
        add: 'Add spread',
        spread: (index: number) => `Spread ${number(index)}`,
        text: 'Spread text',
        prompt: 'Image description in English',
        block: (index: number) => `Text block ${number(index)}`,
        position: 'Position',
        positions: {
          left: 'Left',
          right: 'Right',
          top: 'Top',
          bottom: 'Bottom',
          'top-left': 'Top left',
          'top-right': 'Top right',
          'bottom-left': 'Bottom left',
          'bottom-right': 'Bottom right',
        },
        textColor: 'Text color',
        highlightColor: 'Text background',
        save: 'Save spread',
        saved: 'Spread saved.',
        remove: 'Delete',
        removeConfirm: 'Delete this spread?',
        moveUp: 'Move up',
        moveDown: 'Move down',
      },
    },
    looks: {
      advanced: 'Advanced settings',
      title: 'Consistent details in this book',
      description: 'Outfits, objects, people and places that keep the same design across spreads.',
      drawingReferences:
        'Drawing outfits and recurring details; the illustrations start by themselves.',
      referencesFailed:
        'Some outfits or details could not be drawn. Retry them in the settings and the illustrations carry on.',
      redoCost: 'Another variant of an outfit or detail uses one of the book’s replacements.',
      subjectsTitle: 'Recurring details',
      subjectsHint:
        'Details found on at least two spreads. One approved reference is shared by every scene that shows them.',
      subjectKinds: {
        person: 'Supporting character',
        animal: 'Animal',
        object: 'Object',
        location: 'Location',
        other: 'Other detail',
      },
      noReference: 'No reference image yet',
      afterPayment: 'After payment',
      drawAfterPayment:
        'This detail is not needed for the first free spread. We will draw it after payment, when generating the second and later spreads.',
      generateReference: 'Draw reference',
      drawingReference: 'Drawing the reference…',
      referenceFailed: 'The reference could not be drawn. You can try again.',
      catalogTitle: 'Recurring library items',
      fromLibrary: 'Using the approved library image',
      libraryReferenceNeeded: 'Draw and approve this item in your library.',
      openLibraryItem: 'Open item settings',
      planning: 'Finding outfits and recurring details across the story…',
      preparingTitle: 'Preparing your illustrations',
      preparingHint: 'This helps keep characters and details consistent across every spread.',
      stale: 'Refresh the plan to include the current story, outfits and recurring details.',
      planFailed: 'The outfit plan could not be created. Please try again.',
      guidance: 'Appearance preferences',
      guidancePlaceholder:
        'For example: a blue winter coat; the toy boat is always red; the shopkeeper wears a green hat.',
      replanHint:
        'Unchanged images are kept. Changed outfits and details are drawn again, and their completed scenes will need replacing.',
      plan: 'Plan outfits and details',
      replan: 'Refresh plan',
      noImage: 'This look has not been drawn yet',
      base: 'From the approved character sheet',
      approved: 'Approved',
      needsApproval: 'Needs approval',
      generating: 'Drawing the look…',
      generationFailed: 'The look could not be drawn. You can try again.',
      regenerate: 'Another version',
      generate: 'Draw look',
      approve: 'Approve',
      chooseVariant: 'Select and approve this version',
      continue: 'Draw illustrations',
      spreads: (numbers: string) => `Spreads: ${numbers}`,
    },
    illustrations: {
      title: 'Illustrations',
      empty: 'Add spreads in the Story step first.',
      generateAll: 'Generate all',
      generatingAll: 'Adding to queue…',
      generate: 'Generate',
      regenerate: 'Replace picture',
      editCurrent: 'Edit this exact picture',
      change: 'Change',
      editControls: (index: number) => `Edit spread ${number(index)}`,
      closeControls: (index: number) => `Close editing for spread ${number(index)}`,
      replaceConfirm: (remaining: number) =>
        `Draw this spread again from scratch? This uses one free redo (${number(remaining)} left). The current picture stays among the variants.`,
      replaceConfirmUnlimited:
        'Draw this spread again from scratch? The current picture stays among the variants.',
      replaceHint: 'Draws this spread again from scratch. Uses one free redo.',
      retry: 'Try again',
      regenerationGuidance: {
        label: 'What to change in this picture',
        placeholder: 'For example: add a red scarf and make the background brighter',
        hint: 'We keep this picture and change only what you describe. Uses one free redo.',
      },
      regenerationReference: {
        label: 'Reference image (optional)',
        hint: 'Add a costume, object, or another visual reference for the requested change.',
        uploading: 'Uploading reference…',
        remove: 'Remove',
        issues: {
          too_large: 'The file is larger than 15 MB. Reduce it or choose another file.',
          unsupported_type: 'Use a JPEG, PNG, WebP, or HEIC file.',
          too_small: 'The short side is under 512 px, so some details may be lost.',
          failed: 'Could not upload the reference. Try again.',
        },
      },
      progress: (ready: number, total: number) => `${number(ready)} of ${number(total)} ready`,
      eta: (seconds: number) => `about ${number(Math.max(1, Math.ceil(seconds / 60)))} min`,
      statuses: {
        draft: 'Draft',
        queued: 'Queued',
        generating: 'Drawing…',
        ready: 'Ready',
        failed: 'Error',
      },
      stale: 'The image no longer matches the current story or layout and should be regenerated.',
      promptUpdating: 'Updating the illustration description and visual references…',
      promptFailed: 'The illustration description could not be updated. Retry from the Story step.',
      layout: 'Layout',
      layoutStale: (remaining: number | null | undefined | 'payment') =>
        `The picture was drawn for the current layout: after the change it will no longer fit, and ${enRedrawCost(remaining)}`,
      layoutTextTooLong: (over: number) =>
        `The text won't fit this layout even in the smallest type (about ${number(over)} ${noun(over, 'character', 'characters')} too many) and will cover the picture. Shorten it in the Story step.`,
      layoutChangeQuestion: 'Change the layout?',
      settingsLocked: 'Layout, location and text position open after payment.',
      pageSettings: 'Page layout',
      location: 'Location',
      noLocation: 'No saved location',
      textPosition: (index: number, total = 1) =>
        total === 1 ? 'Where the text goes' : `Where text part ${number(index)} goes`,
      variants: 'Variants',
      zoom: (index: number) => `Enlarge illustration for spread ${number(index)}`,
      noPreview: 'No illustration yet',
      previewAlt: (index: number) => `Illustration for spread ${number(index)}`,
      variantAlt: (index: number) => `Variant for spread ${number(index)}`,
    },
    cover: {
      title: 'Covers',
      subtitle:
        'The title is drawn into the front cover picture. It was fixed when the story was approved.',
      titleLine: (title: string) => `Title on the cover: “${title}”`,
      frontTitle: 'Front cover',
      backTitle: 'Back cover',
      empty: 'No cover yet',
      frontEmpty: 'No front cover yet',
      backEmpty: 'No back cover yet',
      generate: 'Create cover',
      regenerateConfirm:
        'Draw a new version of this cover? The current one stays among the variants.',
      regenerate: 'New variant',
      generating: 'Adding to queue…',
      variants: 'Cover variants',
      previewAlt: 'Selected book cover',
      variantAlt: 'Cover variant',
      backPreviewAlt: 'Selected back cover',
      backVariantAlt: 'Back cover variant',
    },
    video: {
      title: 'Video',
      subtitle:
        'Each spread gets a few seconds of gentle motion. It plays in the reader and on the family link.',
      settings: 'Video settings',
      enabled: 'Create video for this book',
      duration: 'Scene duration',
      seconds: (count: number) => `${number(count)} sec`,
      batchWarningTitle: 'Create video for all spreads?',
      batchWarning: (count: number) =>
        `Video will be created for ${number(count)} spreads. This may take several minutes.`,
      startBatch: 'Animate all spreads',
      startingBatch: 'Starting…',
      waitForSettings: 'Wait for the settings to save first.',
      progress: (ready: number, total: number) =>
        `${number(ready)} of ${number(total)} videos ready`,
      eta: (seconds: number) =>
        `about ${number(Math.max(1, Math.ceil(seconds / 60)))} min remaining`,
      waitingForImage: (count: number) => `waiting for illustration: ${number(count)}`,
      disabled: 'Video is disabled. Existing videos remain available; no new jobs will start.',
      empty: 'Add spreads in the Story step first.',
      prompt: 'Motion and camera instructions in English',
      generate: 'Create video',
      regenerate: 'Regenerate',
      starting: 'Adding to queue…',
      imageRequired: 'Create an illustration for this spread first.',
      variants: 'Video variants',
      variant: (index: number) => `Video variant for spread ${number(index)}`,
      preview: (index: number) => `Video for spread ${number(index)}`,
      imagePreview: (index: number) => `Illustration for spread ${number(index)} video`,
      noPreview: 'No illustration yet',
      statuses: {
        draft: 'Not created',
        queued: 'Queued',
        generating: 'Creating…',
        ready: 'Ready',
        failed: 'Error',
      },
    },
    admin: {
      title: 'AI request log',
      subtitle: 'All model calls, prompt parts, responses, errors, and retries.',
      forbidden: 'This page is available to administrators only.',
      toApp: 'Back to books',
      select: 'Select a request on the left.',
      all: 'All',
      filters: {
        provider: 'Provider',
        model: 'Model',
        status: 'Status',
        target: 'Target',
        sort: 'Sort',
        user: 'User ID',
        book: 'Book ID',
        from: 'From',
        to: 'To',
      },
      targets: {
        character_sheet: 'Character sheet',
        character_look: 'Character look',
        look_plan: 'Outfit plan',
        book_subject: 'Book detail reference',
        thing_ref: 'Thing reference',
        location_ref: 'Location reference',
        scene_image: 'Scene illustration',
        cover: 'Cover',
        back_cover: 'Back cover',
        scene_video: 'Scene video',
        narration: 'Narration',
        story: 'Story',
        scene_prompt: 'Scene prompt',
        collage: 'Collage',
      },
      sorts: { time: 'By time', cost: 'By cost', latency: 'By duration' },
      statuses: {
        queued: 'Queued',
        running: 'Running',
        succeeded: 'Succeeded',
        failed: 'Failed',
        cancelled: 'Cancelled',
      },
      queue: {
        title: 'Queue',
        queued: 'Queued',
        running: 'Running',
        failed: 'Failed',
        succeeded: 'Succeeded',
        empty: 'No failed jobs.',
        retry: 'Retry',
        retried: 'The job was added to the queue again.',
        attempt: (count: number) => `attempt ${number(count)}`,
      },
      promptParts: 'Prompt parts',
      roles: { style: 'Style', subject: 'Scene', layout: 'Layout', suffix: 'Suffix' },
      prompt: 'Final prompt',
      negative: 'Negative prompt',
      seed: 'Seed',
      size: 'Requested size (JSON)',
      parameters: 'Request parameters (JSON)',
      references: 'Reference IDs',
      noReferences: 'No references',
      latency: 'Duration',
      cost: 'Cost',
      noBook: 'No book',
      invalidJson: 'Size and parameters must be JSON objects.',
      replay: 'Retry with edits',
      replayQueued: 'The edited retry has been queued and will be sent to the provider.',
      copyCurl: 'Copy as curl',
      copied: 'curl command copied.',
      reveal: 'Show references',
      referenceAlt: 'Expanded generation reference',
      raw: 'Request, response, and attempts',
      comparison: 'Run comparison',
      noResult: 'No result yet',
      sceneId: 'Spread ID to apply to',
      apply: 'Apply to spread',
      applied: 'Result explicitly applied to the spread.',
      promotions: {
        title: 'Promo codes',
        subtitle: 'Discounts from 0 to 100%, personal grants, and redemption history.',
        create: 'Create promo code',
        name: 'Campaign name',
        code: 'Code',
        generate: 'Generate',
        discount: 'Discount, %',
        targetEmail: 'Only for email',
        anyUser: 'Any user',
        maxTotal: 'Total limit',
        maxPerUser: 'Limit per user',
        unlimited: 'Unlimited',
        startsAt: 'Starts at',
        endsAt: 'Ends at',
        firstPurchase: 'Only the user’s first paid book',
        activateImmediately: 'Activate immediately',
        note: 'Internal note',
        preview: 'Prices with this discount',
        largeWarning:
          'This large discount substantially reduces order revenue. The system will not block it.',
        freeWarning:
          'A 100% code unlocks the book and starts all included generations without a payment provider.',
        freeConfirm:
          'This promo code will unlock a book and start all generations for free. Create it?',
        save: 'Create',
        saving: 'Saving…',
        createError: 'Could not create the promo code. Check the code and dates.',
        active: 'Active',
        paused: 'Paused',
        scheduled: 'Scheduled',
        ended: 'Ended',
        pause: 'Pause',
        resume: 'Resume',
        used: (count: number, limit: number | null) =>
          `used ${number(count)}${limit === null ? '' : ` of ${number(limit)}`}`,
        until: (date: string) => `until ${date}`,
        redeemed: 'Successful redemptions',
        revenue: 'Revenue received',
        discountGiven: 'Discounts granted',
        history: 'Redemption history',
        book: 'Book',
        amount: 'Paid',
        status: 'Status',
        useStatuses: { reserved: 'Awaiting payment', redeemed: 'Redeemed', released: 'Canceled' },
        empty: 'No promo codes yet.',
      },
      accounts: {
        title: 'Accounts',
        subtitle: 'Registered users, their books, and service access.',
        registrationTitle: 'Self-service registration',
        registrationHint: 'When disabled, the registration page shows a placeholder.',
        registrationEnabled: 'Registration is open',
        registrationDisabled: 'Registration is closed',
        registrationUpdated: 'Registration setting saved.',
        economyModeTitle: 'Economy mode',
        economyModeHint:
          'Use standard quality for new MAX-plan illustrations and videos: 1K and 720p instead of 2K and 1080p.',
        economyModeEnabled: 'Economy mode is on',
        economyModeDisabled: 'Economy mode is off',
        economyModeUpdated: 'Economy mode setting saved.',
        createTitle: 'Create account',
        createHint: 'Accounts created here are available regardless of the registration setting.',
        createSubmit: 'Create user',
        createdMessage: 'Account created.',
        accountExists: 'An account with this email already exists.',
        role: 'Role',
        roles: { user: 'User', admin: 'Administrator' },
        markEmailVerified: 'Mark email as verified',
        toGenerations: 'AI log',
        summary: 'Account summary',
        totalAccounts: 'Total accounts',
        totalBooks: 'Total books',
        enabled: 'Enabled',
        disabled: 'Disabled',
        search: 'Search',
        searchPlaceholder: 'Name or email',
        filter: 'Account status',
        sort: 'Sort by',
        direction: 'Direction',
        filters: { all: 'All', enabled: 'Enabled', disabled: 'Disabled' },
        sorts: {
          createdAt: 'Registration date',
          name: 'Name',
          email: 'Email',
          books: 'Book count',
          status: 'Account status',
        },
        directions: { desc: 'Descending', asc: 'Ascending' },
        registered: 'Registered',
        emailVerified: 'email verified',
        emailNotVerified: 'email not verified',
        adminRole: 'Administrator',
        books: 'Books',
        updated: 'Updated',
        showBooks: (count: number) => `Show books (${number(count)})`,
        bookStatuses: {
          draft: 'Draft',
          generating: 'Generating',
          ready: 'Ready',
          archived: 'Archived',
        },
        enable: 'Enable account',
        disable: 'Disable account',
        givePromo: 'Give promo code',
        disableConfirm: (email: string) =>
          `Disable ${email}? All of their active sessions will be ended.`,
        cannotDisableSelf: 'You cannot disable your own account',
        enabledMessage: 'Account enabled.',
        disabledMessage: 'Account disabled and active sessions ended.',
        empty: 'No accounts match the selected filters.',
        openBook: 'Open book',
        adminBookMode:
          'Administrator mode: this book is open as its owner. Changes are saved directly to their book.',
        billingExempt: 'Payments and generation quotas are disabled for administrators.',
        manualEdit: 'Open manual editor',
        replacementLimit: (used: number, total: number) =>
          `Repeat generations: ${number(used)} used of ${number(total)}`,
        replacementUnlimited: 'Repeat generations: unlimited',
        removeReplacementLimit: 'Remove limit',
        restoreReplacementLimit: 'Restore plan limit',
      },
      bookBuilder: {
        title: 'Manual book builder',
        editTitle: 'Manual book editor',
        subtitle:
          'Assemble a finished book from your own text and media. The number of spreads is not limited by a plan.',
        editSubtitle:
          'Edit text and directly replace covers, illustrations, video, and narration in an existing book.',
        owner: 'Book owner',
        ownerLocked: 'The owner is locked after the first file upload.',
        style: 'Style and layout',
        defaultLayout: 'Book layout',
        defaultLayoutHint: 'Applied to every spread without its own override.',
        spreadLayout: 'Spread layout',
        inheritLayout: (layout: string) => `Use book layout — ${layout}`,
        layouts: {
          split: 'Text and image',
          'full-bleed': 'Full-spread image',
          band: 'Horizontal band',
        },
        textPosition: 'Text position',
        primaryTextPosition: 'First text block',
        secondaryTextPosition: 'Second text block',
        positions: {
          left: 'Left',
          right: 'Right',
          top: 'Top',
          bottom: 'Bottom',
          'top-left': 'Top left',
          'top-right': 'Top right',
          'bottom-left': 'Bottom left',
          'bottom-right': 'Bottom right',
        },
        bandImageRatio: 'Image height',
        bookTitle: 'Book title',
        bookSubtitle: 'Subtitle',
        dedication: 'Dedication',
        covers: 'Covers',
        frontCover: 'Front cover',
        backCover: 'Back cover',
        spreads: 'Spreads',
        spreadsHint: 'Text and an illustration are required. Video and narration are optional.',
        addSpread: 'Add spread',
        spread: (index: number) => `Spread ${number(index)}`,
        spreadText: 'Spread text',
        primaryBlockText: 'First block text',
        secondaryBlockText: 'Second block text',
        illustration: 'Illustration',
        videoOptional: 'Video · optional',
        narrationOptional: 'Narration · optional',
        up: 'Move up',
        down: 'Move down',
        remove: 'Remove',
        noFile: 'No file selected',
        chooseFile: 'Choose file',
        replaceFile: 'Replace file',
        clearFile: 'Clear',
        uploading: 'Uploading…',
        readySummary: (spreads: number, files: number) =>
          `${number(spreads)} spreads · ${number(files)} files uploaded`,
        publishHint: 'The book will be added to the owner and receive a public link immediately.',
        publish: 'Build and publish',
        publishing: 'Building book…',
        save: 'Save changes',
        saving: 'Saving…',
        saved: 'Changes saved',
        published: 'Book published',
        openPreview: 'Open preview',
        copyLink: 'Copy link',
        validation: {
          main: 'Choose an owner and style, and enter a title.',
          covers: 'Upload the front and back covers.',
          spread: (index: number) => `Add text and an illustration for spread ${number(index)}.`,
          secondBlock: (index: number) => `Add second-block text for spread ${number(index)}.`,
        },
      },
      usage: {
        title: 'Generation dashboard',
        subtitle:
          'Live statistics for runs, errors, and cost. A repeat is another run of the same target.',
        ranges: { day: '24 hours', week: '7 days' },
        live: 'Current generation state',
        liveUpdated: (date: string) => `Updated ${date}`,
        runningNow: 'Running now',
        queuedNow: 'Queued now',
        refreshHint: 'Data refreshes every 5 seconds.',
        summary: 'Period summary',
        total: 'Total generations',
        regenerations: 'Repeat generations',
        users: 'Active users',
        booksDetail: (count: number) => `${number(count)} books`,
        successRate: 'Success rate',
        failedDetail: (count: number) => `${number(count)} errors`,
        cost: 'Cost, units',
        averageLatency: 'Average duration',
        adminReplaysDetail: (count: number) => `${number(count)} admin replays`,
        timeline: 'Generation activity',
        legend: { total: 'All', failed: 'Errors', regenerations: 'Repeats' },
        chartLabel: 'Generations and repeat generations over time',
        bucketTooltip: (label: string, total: number, regenerations: number, failed: number) =>
          `${label}: ${total} total, ${regenerations} repeats, ${failed} errors`,
        regenerationPoint: (count: number) => `Repeat generations: ${count}`,
        byTarget: 'By generation type',
        targetDetail: (regenerations: number, failed: number, cost: string) =>
          `${regenerations} repeats · ${failed} errors · cost ${cost}`,
        topUsers: 'Most active users',
        user: 'User',
        generationsShort: 'Generations',
        regenerationsShort: 'Repeats',
        errorsShort: 'Errors',
        empty: 'No generations in the selected period.',
      },
    },
    preview: {
      readerLabel: (title: string) => `Read “${title}”`,
      unlockRest: 'This is the free preview · Unlock the whole book',
      backToBuilder: 'Back to editor',
      loadError: 'Could not load the book preview.',
      cover: 'Cover',
      backCover: 'Back cover',
      spread: (index: number) => `Spread ${number(index)}`,
      counter: (current: number, total: number) => `${number(current)} of ${number(total)}`,
      fullscreen: 'Full screen',
      exitFullscreen: 'Exit full screen',
      previous: 'Previous page',
      next: 'Next page',
      navigationLabel: 'Book navigation',
      pagePicker: 'Choose page',
      help: 'Drag a page by its edge, use the arrow keys, or swipe.',
      coverAlt: (title: string) => `Cover illustration for “${title}”`,
      backCoverAlt: (title: string) => `Back cover illustration for “${title}”`,
      sceneAlt: (index: number, title: string) =>
        `Illustration for spread ${number(index)} of “${title}”`,
      playNarration: 'Play narration',
      pauseNarration: 'Pause',
      resumeNarration: 'Resume',
      repeatNarration: 'Repeat spread',
      muteNarration: 'Mute',
      unmuteNarration: 'Unmute',
      narrationUnavailable: 'No narration for this spread',
    },
    narration: {
      title: 'Narration',
      createVoice: 'Create a new voice',
      newVoiceName: 'New voice name',
      voiceName: 'For example, Mom’s voice',
      addVoice: 'Add voice',
      creatingVoice: 'Creating voice…',
      voiceConsent:
        'I confirm that the voice owner consents to processing this recording and creating a copy of their voice to read the book.',
      voiceConsentLink: 'Consent terms',
      sampleHint:
        'This sample takes about 20 seconds: one voice, a quiet room, no music or filters.',
      referenceScriptLabel: 'Text to read',
      referenceScript:
        'I am reading this text in a calm, slow, and natural voice. This is exactly how I want this book to be narrated. Let every word sound clear, gentle, and warm, while the story unfolds unhurriedly, as though we were turning its pages together.',
      referenceScriptHint: 'Read naturally, without rushing or leaving long pauses.',
      sampleTranscript: 'What the person says in the recording (optional)',
      sampleTranscriptPlaceholder: 'Enter the exact words if you know them',
      transcriptExact: 'This text matches the recording word for word',
      transcriptHint:
        'We send an exact transcript with the audio to improve voice matching. If you are not sure, leave the box unchecked.',
      chooseSample: 'Choose audio file',
      replaceChosenSample: 'Choose another file',
      sampleReady: (fileName: string) => `Sample ready: ${fileName}`,
      sampleReadyLength: (seconds: number) => `Sample ready: ${number(seconds)} s`,
      sampleTooShort: (seconds: number) =>
        `This recording is ${number(seconds)} s. A sample needs at least 6 seconds: read the whole text.`,
      sampleTooLong: (seconds: number) =>
        `This recording is ${number(seconds)} s. A sample can be at most 30 seconds.`,
      voiceNeedsSample: 'No sample yet: add one to use this voice.',
      editVoice: 'Edit',
      renameVoice: 'Voice name',
      saveVoiceName: 'Save name',
      recordSample: 'Record sample',
      rerecordSample: 'Record again',
      uploadSample: 'Or choose an audio file',
      replaceSample: 'Replace with an audio file',
      deleteVoice: 'Delete voice',
      deleteVoiceConfirm: (voiceName: string) =>
        `Delete the voice “${voiceName}”? Existing narration will remain in the book.`,
      voiceForSynthesis: 'Narration voice',
      femaleVoice: 'Female voice',
      maleVoice: 'Male voice',
      previewVoice: (voiceName: string) => `Listen to “${voiceName}”`,
      pauseVoicePreview: (voiceName: string) => `Pause “${voiceName}”`,
      previewAfterSynthesis: 'Narrate one spread to hear this voice before doing the whole book.',
      exactTranscriptSaved: 'An exact transcript is saved with this sample.',
      useOwnVoice: 'Use my own voice',
      uploadInstead: 'or upload a file',
      recordInstead: 'or record here',
      fileContents: 'What is in the file?',
      fileIsScript: 'Me reading the text above',
      fileHowTo: 'Read the text into any voice-memo app on your phone, then upload that file.',
      fileIsOther: 'Another recording',
      editVoiceNamed: (voiceName: string) => `Edit “${voiceName}”`,
      closeVoiceNamed: (voiceName: string) => `Close “${voiceName}”`,
      listen: 'Listen',
      pausePreview: 'Pause',
      bySpread: 'Or narrate spread by spread',
      generateAll: 'Narrate the whole book',
      replaceRecordedConfirm: (count: number) =>
        count === 1
          ? 'One spread has your own recording. Narrating the whole book replaces it with the chosen voice. Continue?'
          : `${number(count)} spreads have your own recordings. Narrating the whole book replaces them with the chosen voice. Continue?`,
      spread: (index: number) => `Spread ${number(index)}`,
      empty: 'Add spreads in the Story step first.',
      stale:
        'The text changed after recording. The old narration still plays, but should be updated.',
      record: 'Record',
      rerecord: 'Record again',
      uploadAudio: 'Upload audio',
      replaceAudio: 'Replace audio',
      stopRecording: 'Stop recording',
      microphoneDenied:
        'Microphone access failed. Allow access in your browser settings and try again.',
      synthesize: 'Synthesize',
      resynthesize: 'Synthesize again',
      remove: 'Delete narration',
      actionFailed: 'Could not complete the narration action. Try again.',
      statuses: {
        draft: 'Not narrated',
        queued: 'Queued',
        generating: 'Narrating…',
        ready: 'Ready',
        failed: 'Error',
      },
    },
    export: {
      title: 'Share and download',
      formatTitle: 'File format',
      formats: {
        pdf_print: {
          title: 'Print PDF',
          description: 'Separate pages for printing: front cover, every page, back cover.',
        },
        pdf_screen: {
          title: 'Screen PDF',
          description: 'One spread per page, without bleed or blank pages.',
        },
        png_zip: {
          title: 'PNG archive',
          description: 'Every physical page as a separate PNG in a ZIP archive.',
        },
      },
      create: 'Create file',
      submitting: 'Adding to queue…',
      cached: 'The book has not changed, so the ready file was loaded from cache.',
      failed: 'Could not export the book. Try again.',
      retry: 'Try again',
      history: 'Export history',
      empty: 'No exports yet.',
      emptyHint: 'Your PDF will appear here once it’s built.',
      download: 'Download',
      progress: (value: number) => `${number(value)}% complete`,
      linkExpires: (time: string) =>
        `This download link works until ${time}. After that, press Download again for a fresh one.`,
      status: {
        queued: 'Queued',
        running: 'Building',
        succeeded: 'Ready',
        failed: 'Error',
        cancelled: 'Cancelled',
      },
    },
    publish: {
      title: 'Family link',
      subtitle:
        'Publish the book to get a read-only link you can share. Readers will not need to sign in.',
      locksEditing: 'While it is published, the book can’t be edited.',
      unpublishedHint: 'There is no public link until the book is published.',
      publish: 'Publish',
      publishing: 'Publishing…',
      unpublish: 'Unpublish',
      unpublishing: 'Unpublishing…',
      published: (date: string) => `Published ${date}.`,
      linkLabel: 'Family link',
      readyTitle: 'Your book is ready for readers',
      readyText: 'Share it with family or show the QR code — readers will not need an account.',
      share: 'Share',
      shareText: (title: string) => `Open the book “${title}”`,
      shared: 'Book shared',
      copy: 'Copy link',
      copied: 'Link copied',
      copyFailed: 'Couldn’t copy the link',
      showQr: 'Show QR code',
      qrEyebrow: 'A story always nearby',
      qrTitle: 'Book QR code',
      qrDescription: 'Show the code on another device or download a ready-to-share card.',
      qrInstruction: 'Point the camera here to open the book',
      qrAlt: (title: string) => `QR code for “${title}”`,
      downloadQr: 'Download card',
      qrStableHint: 'The QR uses the book’s permanent link and stays the same after republishing.',
      open: 'Open as reader',
      lockNotice:
        'This book is published, so it can’t be edited. To make changes, unpublish it in the Export step.',
      stableLinkHint:
        'Each book has one stable link. The same link works again after republishing.',
      notPublishable: 'This book has no spreads yet. Create the story before publishing.',
      consent: {
        label:
          'I agree to share this book by link, and I have permission from everyone pictured or voiced in it.',
        documentLink: 'Consent text',
        hint: 'Anyone who receives the link can open it and pass it on. You can unpublish at any time.',
      },
      failed: 'Could not change publication status. Try again.',
    },
    publicBook: {
      loading: 'Opening book…',
      notFoundTitle: 'Book unavailable',
      notFoundText:
        'The link is invalid or the book has been unpublished. Ask the sender for a new link.',
      home: `About ${name}`,
      backToLanding: 'Back to home',
      reportLabel: 'Report this book:',
      reportLink: 'How reports are handled',
      makeYourOwn: 'Make your own book',
    },
    report: {
      title: 'Report a problem',
      intro: `If a book published through ${name} shows something that should not be online — a photograph used without consent, a child in a sexual context, anything you believe to be unlawful — tell us. You do not need an account, and you do not have to give your name.`,
      howTitle: 'Where to write',
      howText:
        'Send the book link if you have one, and whatever you know about what you saw. A link is enough for us to act; without one the report is still worth sending.',
      nextTitle: 'What happens next',
      next: [
        'Access to the book is closed as soon as we understand the report.',
        'The data behind it is preserved even if you ask us to delete it: once we know, the law requires us to keep it as evidence, and deleting at that point is a violation of its own.',
        'We tell you what we did. Where the law requires us to report the find ourselves, we do.',
      ],
      responseTitle: 'What happens to it',
      responseText: 'A person reads every report and acts on it.',
      privacyTitle: 'About you',
      privacyText:
        'A report is a message to us, not a publication: we do not disclose who sent it.',
    },
    monetization: {
      chooseTitle: 'Choose your book format',
      chooseSubtitle:
        'The plan sets the story length and finished-book features. Payment comes after the first spread.',
      popular: 'Most popular',
      pagesShort: (count: number) => `${number(count)} pages`,
      selected: 'Selected',
      select: 'Select',
      lockedTitle: 'Plan',
      lockedError: 'The story is already written for this plan, so the plan can’t change now.',
      locked: (pages: number) =>
        `The story was written for ${number(pages)} ${noun(pages, 'page', 'pages')}, so the plan can’t change now.`,
      summaryPaid: (price: string) => `${price}, paid`,
      summaryUnpaid: (price: string) => `${price}, paid after the free first spread`,
      included: 'What’s included',
      planLimit: (used: number, limit: number) =>
        `${number(used)} of ${number(limit)} in this plan`,
      features: {
        pages: (count: number, spreads: number) =>
          `${number(count)} ${noun(count, 'page', 'pages')}: ${number(spreads)} ${noun(spreads, 'spread', 'spreads')} and the covers`,
        characters: (count: number) =>
          `Up to ${number(count)} ${noun(count, 'character', 'characters')}`,
        props: (things: number, locations: number) =>
          `Up to ${number(things)} ${noun(things, 'thing', 'things')} and ${number(locations)} ${noun(locations, 'location', 'locations')}`,
        resolution: (value: string) =>
          value === '2K' ? 'Enhanced illustration quality' : 'Standard illustration quality',
        video: (resolution: string) =>
          resolution === '1080p' ? 'Enhanced-quality video' : 'Standard-quality video',
        noVideo: 'No video',
        narration: 'Narration in a familiar voice',
        noNarration: 'No narration',
        share: 'Screen PDF and family link',
        printStandard: 'Standard-quality print layout',
        printHigh:
          brand.printHighQuality['en'] ?? brand.printHighQuality[brand.defaultLocale] ?? '',
        noPrint: 'No print-ready PDF',
        replacements: (count: number, withVideo: boolean) =>
          `${number(count)} free ${noun(count, 'redo', 'redos')} of illustrations${withVideo ? ' and videos' : ''}`,
      },
      plans: {
        book: {
          name: 'Book',
          summary: 'A classic personalized book for screen reading and printing.',
        },
        live_book: {
          name: 'Living Book',
          summary: 'A personalized book with animated illustrations.',
        },
        gift_book: {
          name: 'Gift Book',
          summary: 'The complete edition with narration and a high-quality print layout.',
        },
      },
      preview: {
        title: 'Creating your first spread for free',
        text: 'Once it is ready, you can see the result and continue creating the book.',
        readyTitle: 'Your free spread is ready',
        readyText: (remaining: number, plan: string) =>
          `Like it? The ${plan} plan draws the other ${number(remaining)} spreads, both covers and everything else it includes.`,
        lockedSpread: (index: number) => `Spread ${number(index)} · drawn after payment`,
      },
      payment: {
        title: 'Your first spread is ready',
        text: (remaining: number, plan: string) =>
          `Your story is saved. Once you pay, we draw the other ${number(remaining)} spreads, both covers and everything else in the ${plan} plan.`,
        required: 'This opens after payment.',
        textFree: (remaining: number, plan: string) =>
          `Your story is saved. Once you claim the book, we draw the other ${number(remaining)} spreads, both covers and everything else in the ${plan} plan.`,
        previewAlt: 'Your free spread',
        oneTime: 'One-time payment · No subscription',
        alternativePrice: (price: string, currency: string) =>
          `Paying from a country that uses ${currency}? You’ll be charged ${price} instead.`,
        action: (price: string) => `Pay ${price}`,
        processing: 'Opening checkout…',
        error: 'Could not continue. Please try again.',
        promoToggle: 'Have a promo code?',
        promoLabel: 'Promo code',
        promoPlaceholder: 'Enter code',
        promoApply: 'Apply',
        promoApplied: (percent: number) => `Promo code applied · ${number(percent)}% off`,
        promoPlanHint: 'The price is updated. Check the selected plan before checkout.',
        promoUnlocked: 'The book is available without payment.',
        promoInvalid: 'This promo code is invalid or no longer available.',
        /** The server got no checkbox: only a page opened before it existed sends that. */
        freeAction: 'Get the book for free',
        freeProcessing: 'Unlocking the book…',
        returnPending: brand.paymentProviderName
          ? `Checking the payment with ${brand.paymentProviderName}…`
          : 'Checking the payment…',
        returnSucceeded: 'Your book is unlocked. Everything in its plan is now available.',
        returnCanceled: 'The payment was canceled. You can try again.',
        returnError: 'Could not verify the payment. Refresh the page or try again later.',
        lockedStep: {
          cover: 'Both covers are drawn after payment, from the same characters and style.',
          video: 'Every spread is animated after payment.',
          narration: 'Narration opens after payment: pick a voice or record your own.',
          export: 'After payment you can share the family link and download the PDF.',
        },
        goToPay: 'See the free spread and unlock the book',
        goToPreview: 'Preview the first spread',
        /** Clickwrap: an unticked box is a refusal, so the sentence is a checkbox label. */
        legalPrefix: 'I accept the',
        offerLink: legalLink('paywallTerms'),
        legalJoin: 'and the',
        refundLink: legalLink('paywallRefund'),
      },
      replacementsLeft: (remaining: number, total: number) =>
        `Free redos left: ${number(remaining)} of ${number(total)}`,
      replacementPack: {
        title: 'Included changes are used up',
        text: (count: number) =>
          `Add ${number(count)} more ${noun(count, 'change', 'changes')} to illustrations and videos of this book. Return to the change after payment.`,
        action: (price: string) => `Add for ${price}`,
        processing: 'Opening checkout…',
        error: 'Could not add the changes. Please try again.',
      },
      unavailable: 'This feature is not included in the selected plan.',
      unavailableStep: {
        video: 'In plans with video, every spread gets a few seconds of gentle motion.',
        narration: 'In plans with narration, the book is read aloud in a voice you choose.',
      },
    },
    builder: {
      back: 'All books',
      preview: 'Preview',
      navigationLabel: 'Book creation steps',
      mainTitle: 'Basics',
      save: {
        saved: 'Saved',
        waiting: 'Unsaved changes…',
        saving: 'Saving…',
        error: 'Could not save',
      },
      fields: {
        title: 'Working title',
        subtitle: 'Subtitle',
        occasion: 'Occasion',
        age: 'Reader’s age',
        ageHint: 'Who the book is read to. The story’s words and length are pitched at this age.',
      },
      style: 'Picture style',
      styleSuggestionsLabel: 'Style suggestions',
      styleSuggestions: {
        hyperrealistic: 'Hyperrealistic',
        anime: 'Anime',
        softCartoon: 'Soft cartoon (Pixar-inspired)',
        watercolor: 'Watercolor',
        flat: 'Flat children’s illustration',
        classic: 'Classic storybook illustration',
      },
      ownStyle: 'Describe your own style',
      ownStyleLabel: 'How should the pictures look?',
      ownStyleHint:
        'In your own words: the technique, the colours, the mood. The first illustrations show your own style.',
      ownStylePlaceholder: 'Soft watercolour, muted colours, like an old fairy-tale book',
      ownStyleSave: 'Use this style',
      ownStyleYours: 'Your style',
      styleLocked: 'The style was locked by the first completed illustration.',
      layout: 'Book layout',
      layoutHints: {
        split: 'Text on one page, the picture on the other',
        'full-bleed': 'One picture across both pages, text on top',
        band: 'A wide picture with the text in a strip below',
      },
      layoutNames: {
        split: 'Text + illustration',
        'full-bleed': 'Full spread',
        band: 'Panorama with a band',
      },
      alternateSides: 'Alternate the text page between left and right',
      layoutConfirm: (count: number) =>
        `Changing the layout will make ${number(count)} illustrations outdated. Continue?`,
      steps: {
        main: 'Basics',
        characters: 'Characters',
        things: 'Favorite things',
        locations: 'Locations',
        story: 'Story',
        illustrations: 'Illustrations',
        cover: 'Covers',
        video: 'Video',
        narration: 'Narration',
        export: 'Share and download',
      },
      stepStates: {
        empty: 'Empty',
        working: 'In progress',
        ready: 'Ready',
        error: 'Needs attention',
        optional: 'Optional',
        unavailable: 'Not in this plan',
      },
      locked: {
        illustrations:
          'Illustrations become available after every character is approved in this book’s style.',
        illustrationsCount: (pending: number, total: number) =>
          `${number(total - pending)} of ${number(total)} characters approved.`,
        noCharacters: 'Add at least one character first.',
      },
      soon: 'This step is coming in a future update.',
      preStoryReadOnly:
        'The story is approved, so this step is now read-only. You can still edit the text in the Story step.',
      goToStep: (label: string) => `Go to “${label}”`,
      nextStep: (label: string) => `Next: ${label}`,
    },
  };
}
